import React, { useState } from "react";
import type { FC, ReactNode } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Collapse,
  ListItem,
  ListItemProps,
  Tooltip,
} from "@mui/material";
import ChevronDownIcon from "../icons/ChevronDown";
import ChevronRightIcon from "../icons/ChevronRight";
import { useTheme, useMediaQuery } from "@mui/material";

interface NavItemProps extends ListItemProps {
  active?: boolean;
  children?: ReactNode;
  depth: number;
  icon?: React.ReactElement;
  info?: ReactNode;
  open?: boolean;
  path?: string;
  title: string;
  collapsed?: boolean;
}

const NavItem: FC<NavItemProps> = (props) => {
  const {
    active,
    children,
    depth,
    icon,
    info,
    open: openProp,
    path,
    title,
    collapsed,
    ...other
  } = props;
  const theme = useTheme();
  const tabletDevice = useMediaQuery(theme.breakpoints.down("lg"));

  const [open, setOpen] = useState<boolean>(openProp);

  const handleToggle = (): void => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 16;
  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const iconOnly = React.useMemo(() => {
    if (tabletDevice) return false;
    return collapsed && !children;
  }, [collapsed, children, tabletDevice]);

  // Branch
  if (children) {
    return (
      <ListItem disableGutters sx={{ display: "block", py: 0 }} {...other}>
        <Tooltip title={collapsed ? title : ""} placement="right">
          <Button
            endIcon={
              !open ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ChevronDownIcon fontSize="small" />
              )
            }
            onClick={handleToggle}
            startIcon={icon}
            sx={{
              color: active ? "primary.main" : "text.secondary",
              "& .MuiButton-startIcon": {
                mr: collapsed ? 0 : 1,
              },
              fontWeight: "fontWeightMedium",
              justifyContent: collapsed ? "center" : "flex-start",
              pl: collapsed ? 1 : `${paddingLeft}px`,
              pr: "8px",
              py: "12px",
              textAlign: "left",
              textTransform: "none",
              width: "100%",
              minHeight: 40,
              borderRadius: 1.5,
            }}
            variant="text"
          >
            {(!collapsed || tabletDevice) && (
              <Box
                component="span"
                sx={{
                  display: "inline-block",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  maxWidth: collapsed && !tabletDevice ? 0 : 200,
                  opacity: collapsed && !tabletDevice ? 0 : 1,
                  transform:
                    collapsed && !tabletDevice
                      ? "translateX(-8px)"
                      : "translateX(0)",
                  transition: `max-width 0.3s ease, opacity 0.3s ease ${collapsed && !tabletDevice ? "0s" : "0.4s"}, transform 0.3s ease ${collapsed && !tabletDevice ? "0s" : "0.4s"}`,
                }}
              >
                {title}
              </Box>
            )}
            {info}
          </Button>
        </Tooltip>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  // Leaf
  return (
    <ListItem disableGutters sx={{ display: "flex", py: 0 }}>
      <Tooltip title={iconOnly ? title : ""} placement="right">
        <Button
          component={path && RouterLink}
          startIcon={
            icon
              ? React.cloneElement(icon, {
                  fontSize: "medium", // or manually set style={{ fontSize: 24 }}
                })
              : undefined
          }
          sx={{
            color: active ? "primary.main" : "text.secondary",
            fontWeight: active ? 600 : 400,
            justifyContent: collapsed ? "center" : "flex-start",
            textAlign: "left",
            pl: collapsed ? 1 : `${paddingLeft}px`,
            pr: "6px",
            py: "12px",
            textTransform: "none",
            width: tabletDevice ? "100%" : `calc(100% - ${paddingLeft}px)`,
            minHeight: 40,
            borderRadius: 1.5,
            "& svg": {
              color: active ? "primary.main" : "text.secondary",
              fontSize: 22,
            },
            "&::before": active && {
              content: '""',
              position: "absolute",
              left: 0,
              top: 8,
              bottom: 8,
              width: 3,
              backgroundColor: "primary.main",
              borderRadius: 1,
            },
          }}
          variant="text"
          to={path}
        >
          {(!collapsed || tabletDevice) && (
            <Box sx={{ flexGrow: 1 }}>{title}</Box>
          )}
          {/* {collapsed && tabletDevice && <Box sx={{ flexGrow: 1 }}>{title}</Box>} */}
          {info}
        </Button>
      </Tooltip>
    </ListItem>
  );
};

export default NavItem;
